import Menu from "@mui/icons-material/Menu";
import MenuOpen from "@mui/icons-material/MenuOpen";
import { Box, IconButton, Stack } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import { alpha, styled } from "@mui/material/styles";
import { Fragment } from "react";

import EncampLogo from "../../assets/EncampLogo.svg";
import {
  type GroupChildNavItem,
  type GroupNavItem,
  NavItemV2,
  type SectionNavItem,
  type StandardNavItem,
} from "./NavItemV2";

type CustomNavItem = {
  kind: "CustomElement";
  customElement: React.JSX.Element;
};

export type NavItem =
  | StandardNavItem
  | GroupChildNavItem
  | SectionNavItem
  | GroupNavItem
  | CustomNavItem;

interface NavDrawerProps {
  navItems: NavItem[];
  collapsed: boolean;
  expandedWidth: string;
  collapsedWidth: string;
  onNavToggle: () => void;
}

// Use stable keys so each nav row keeps the correct persisted expansion state.
function getNavItemKey(navItem: NavItem, idx: number) {
  switch (navItem.kind) {
    case "CustomElement":
      return `custom-${idx}`;
    case "Group":
      return `group-${navItem.text}-${navItem.defaultPath}`;
    case "Section":
      return `section-${navItem.text}-${idx}`;
    case "Standard":
    case "GroupChild":
      return `${navItem.kind}-${navItem.text}-${navItem.path ?? idx}`;
  }
}

const DrawerScrollArea = styled(Box)(({ theme }) => {
  const thumbBase =
    theme.palette.mode === "light" ? "#878D99" : alpha(theme.palette.text.primary, 0.38);
  const thumbHover =
    theme.palette.mode === "light" ? "#A0A6B2" : alpha(theme.palette.text.primary, 0.52);
  const trackBase = "transparent";
  const scrollbarWidth = 10;

  return {
    width: "100%",
    height: "100%",
    minHeight: 0,
    position: "relative",
    overflowY: "auto",
    backgroundColor: theme.palette.mode === "light" ? "#051E3C" : theme.palette.background.default,
    // Reserve gutter space where supported so the layout does not jump when the scrollbar is revealed.
    scrollbarGutter: "stable",
    // Keep the gutter reserved and change colors on hover instead of changing scrollbar width.
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    // WebKit scrollbar selectors remain non-standard, so treat them as a progressive enhancement.
    "&:hover": {
      scrollbarWidth: "thin",
      scrollbarColor: `${thumbBase} ${trackBase}`,
    },

    "&::-webkit-scrollbar": {
      width: scrollbarWidth,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "transparent",
      borderRadius: 999,
      // Keep the thumb visually inset instead of filling the entire track width.
      border: "2px solid transparent",
      backgroundClip: "padding-box",
    },
    "&:hover::-webkit-scrollbar-thumb": {
      backgroundColor: thumbBase,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: thumbHover,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: trackBase,
      borderRadius: 999,
    },
    "&::-webkit-scrollbar-corner": {
      backgroundColor: "transparent",
    },
  };
});

export function LeftNavDrawerV2({
  navItems,
  collapsed,
  expandedWidth,
  collapsedWidth,
  onNavToggle,
}: NavDrawerProps) {
  const drawerWidth = collapsed ? collapsedWidth : expandedWidth;
  const navToggleLabel = collapsed ? "Expand navigation" : "Collapse navigation";

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          overflowX: "hidden",
          boxSizing: "border-box",
          borderRight: "none",
          transition: (theme) =>
            theme.transitions.create(["width"], {
              easing: collapsed ? theme.transitions.easing.sharp : theme.transitions.easing.easeOut,
              duration: collapsed
                ? theme.transitions.duration.leavingScreen
                : theme.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      <DrawerScrollArea
        data-testid="left-nav-drawer"
        sx={{
          width: drawerWidth,
        }}
      >
        <Box component="nav" aria-label="Primary navigation" sx={{ height: "100%" }}>
          <Stack
            sx={{
              height: collapsed ? "112px" : "176px",
              marginY: collapsed ? 1 : 0,
              transition: (theme) =>
                theme.transitions.create(["height", "margin"], {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.shorter,
                }),
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginLeft: "auto",
                marginRight: "auto",
                marginTop: "auto",
                marginBottom: "auto",
              }}
            >
              <img
                src={EncampLogo}
                alt="Encamp logo"
                style={{
                  width: collapsed ? "28px" : "80px",
                  transition: "width 0.2s ease-in-out",
                }}
              />
            </Box>
            <IconButton
              aria-label={navToggleLabel}
              onClick={onNavToggle}
              sx={{
                color: "white",
                width: collapsed ? 40 : 44,
                height: collapsed ? 40 : 44,
                marginX: "auto",
                marginBottom: collapsed ? 0 : 1,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.06)",
                },
              }}
            >
              {collapsed ? (
                <Menu sx={{ fontSize: "1.375rem" }} />
              ) : (
                <MenuOpen sx={{ fontSize: "1.375rem" }} />
              )}
            </IconButton>
          </Stack>
          <List sx={{ paddingTop: 0 }}>
            {navItems.map((navItem, idx) => {
              const navItemKey = getNavItemKey(navItem, idx);

              if (navItem.kind === "CustomElement") {
                if (collapsed) {
                  return null;
                }

                return <Fragment key={navItemKey}>{navItem.customElement}</Fragment>;
              } else {
                return <NavItemV2 key={navItemKey} navItem={navItem} collapsed={collapsed} />;
              }
            })}
          </List>
        </Box>
      </DrawerScrollArea>
    </Drawer>
  );
}
